// Belgian or Indian way of trading (Settings › Custom Options › Trading
// System). Belgian = no tax per invoice, the app as it always was. Indian =
// two discounts per stone (Less 1 %, Less 2 %) and GST once per invoice:
// CGST + SGST (same state) or IGST (another state), never both.
// Missing setting = Belgian, so every existing company is unchanged.

import type { Business, BusinessSettings } from '../types';

export type TradingSystem = 'belgian' | 'indian';

export function tradingSystemOf(settings: Pick<BusinessSettings, 'tradingSystem'> | null | undefined): TradingSystem {
  return settings?.tradingSystem === 'indian' ? 'indian' : 'belgian';
}
export function isIndian(settings: Pick<BusinessSettings, 'tradingSystem'> | null | undefined): boolean {
  return tradingSystemOf(settings) === 'indian';
}

/** Settings with the Admin Portal's choice laid over them: the company
 *  record's tradingSystem wins when set, so a company can't switch itself. */
export function withBusinessSystem<T extends Pick<BusinessSettings, 'tradingSystem'>>(
  settings: T, business: Pick<Business, 'tradingSystem'> | null | undefined,
): T {
  const ts = business?.tradingSystem;
  return ts && ts !== settings.tradingSystem ? { ...settings, tradingSystem: ts } : settings;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** A price after Less 1 % and then Less 2 % (each on what's left). */
export function lineNet(gross: number, less1?: number | null, less2?: number | null): number {
  const a = less1 && less1 > 0 && less1 < 100 ? less1 : 0;
  const b = less2 && less2 > 0 && less2 < 100 ? less2 : 0;
  return gross * (1 - a / 100) * (1 - b / 100);
}

/** GST % on one invoice. CGST + SGST, or IGST: never both. */
export interface GstPercents { cgstPct?: number | null; sgstPct?: number | null; igstPct?: number | null }
export interface GstAmounts { cgst: number; sgst: number; igst: number; total: number }

/** Which side is in use: 'split' (CGST / SGST), 'igst', or 'none'. */
export function gstMode(p: GstPercents): 'split' | 'igst' | 'none' {
  if ((p.cgstPct ?? 0) > 0 || (p.sgstPct ?? 0) > 0) return 'split';
  if ((p.igstPct ?? 0) > 0) return 'igst';
  return 'none';
}

/** True when both sides are filled (not allowed). */
export function gstConflict(p: GstPercents): boolean {
  return ((p.cgstPct ?? 0) > 0 || (p.sgstPct ?? 0) > 0) && (p.igstPct ?? 0) > 0;
}

/** GST on a taxable amount. A conflict counts only the CGST / SGST side. */
export function invoiceGst(taxable: number, p: GstPercents): GstAmounts {
  const mode = gstMode(p);
  const cgst = mode === 'split' ? round2(taxable * (p.cgstPct ?? 0) / 100) : 0;
  const sgst = mode === 'split' ? round2(taxable * (p.sgstPct ?? 0) / 100) : 0;
  const igst = mode === 'igst' ? round2(taxable * (p.igstPct ?? 0) / 100) : 0;
  return { cgst, sgst, igst, total: round2(cgst + sgst + igst) };
}

/**
 * Starting GST % for a new invoice: the Settings rate, split half CGST /
 * half SGST when the party is in our state (or either state is unknown),
 * else all IGST.
 */
export function defaultGst(settings: Pick<BusinessSettings, 'gst'> | null | undefined, partyState?: string | null): GstPercents {
  const rate = settings?.gst?.defaultRate ?? 0;
  if (!(rate > 0)) return {};
  const ours = settings?.gst?.state?.trim().toLowerCase();
  const theirs = partyState?.trim().toLowerCase();
  if (ours && theirs && ours !== theirs) return { igstPct: rate };
  return { cgstPct: rate / 2, sgstPct: rate / 2 };
}

/** States and union territories of India (for GST place of supply). */
export const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

export const NO_GST: GstAmounts = { cgst: 0, sgst: 0, igst: 0, total: 0 };

/** The GST fields saved on an invoice (all null when there is no GST, so an edit clears them). */
export function gstInvoiceFields(taxable: number, p: GstPercents, t: GstAmounts) {
  return t.total > 0
    ? { gst: { cgstPct: p.cgstPct ?? null, sgstPct: p.sgstPct ?? null, igstPct: p.igstPct ?? null },
        taxableUSD: round2(taxable), cgstUSD: t.cgst, sgstUSD: t.sgst, igstUSD: t.igst, taxTotalUSD: t.total }
    : { gst: null, taxableUSD: null, cgstUSD: null, sgstUSD: null, igstUSD: null, taxTotalUSD: null };
}

/**
 * Separate due dates: GST goes on each due date in step with its stones
 * (the last one takes the rounding). Changes the groups in place.
 */
export function spreadGstOverGroups<G extends { amountUSD: number }>(groups: G[] | undefined, taxTotal: number, goods: number): G[] | undefined {
  if (!groups || groups.length === 0 || !(taxTotal > 0) || !(goods > 0)) return groups;
  let left = taxTotal;
  groups.forEach((g, i) => {
    const share = i === groups.length - 1 ? left : round2(taxTotal * g.amountUSD / goods);
    left = round2(left - share);
    g.amountUSD = round2(g.amountUSD + share);
  });
  return groups;
}

export type PaidGstLike = { gstTotalUSD?: number | null; gstNotClaimable?: boolean | null; gstReverseCharge?: boolean | null } | null | undefined;

/**
 * How a paid amount's GST is booked (see linesExpense):
 * - normal: `inputTaxUSD` = the claimable GST inside the amount paid.
 * - reverse charge: nothing inside the amount; `reverseCharge` = the GST we
 *   owe ourselves, claimable or not.
 */
export function paidGstPosting(x: PaidGstLike): { inputTaxUSD: number; reverseCharge?: { taxUSD: number; claimable: boolean } } {
  const tax = x?.gstTotalUSD ?? 0;
  if (!(tax > 0)) return { inputTaxUSD: 0 };
  if (x?.gstReverseCharge) return { inputTaxUSD: 0, reverseCharge: { taxUSD: tax, claimable: !x.gstNotClaimable } };
  return { inputTaxUSD: x?.gstNotClaimable ? 0 : tax };
}

/** GST on a broker payout, as linesBrokerPayment takes it (undefined when none). */
export function brokerGstOf(x: PaidGstLike): { taxUSD: number; claimable: boolean; reverseCharge: boolean } | undefined {
  const tax = x?.gstTotalUSD ?? 0;
  return tax > 0 ? { taxUSD: tax, claimable: !x?.gstNotClaimable, reverseCharge: !!x?.gstReverseCharge } : undefined;
}

/**
 * GST on a return (Purchase Return / credit note): the invoice's own % on the
 * goods coming back, so it is taken back exactly the way the invoice charged
 * it (CGST + SGST, or IGST). Null when the invoice had no GST (Belgian, or an
 * Indian invoice saved without it): the return is then goods only, as before.
 */
export function returnGst(inv: { gst?: GstPercents | null; taxTotalUSD?: number | null }, goods: number) {
  if (!(inv.taxTotalUSD && inv.taxTotalUSD > 0) || !inv.gst || !(goods > 0)) return null;
  const t = invoiceGst(goods, inv.gst);
  return t.total > 0 ? gstInvoiceFields(goods, inv.gst, t) : null;
}

/** What a return takes off the invoice: the goods plus their GST. */
export function returnCreditOf(goods: number | null | undefined, r: { taxTotalUSD?: number | null }): number {
  return round2((goods ?? 0) + (r.taxTotalUSD ?? 0));
}

/** The three GST boxes on a form, as typed. */
export interface GstInput { cgst: string; sgst: string; igst: string }
const pctStr = (v?: number | null) => (v != null && v > 0 ? String(v) : '');
const pctNum = (v: string) => { const n = parseFloat(v); return n > 0 ? n : undefined; };
/** Saved / default percents → the three boxes. */
export function gstInputFrom(p?: GstPercents | null): GstInput {
  return { cgst: pctStr(p?.cgstPct), sgst: pctStr(p?.sgstPct), igst: pctStr(p?.igstPct) };
}
/** The three boxes → percents (empty = none). */
export function gstPercentsOf(g: GstInput): GstPercents {
  return { cgstPct: pctNum(g.cgst), sgstPct: pctNum(g.sgst), igstPct: pctNum(g.igst) };
}
